/**
 * @file useOpenInSearch.js
 * @version 1.1.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-30
 * @modified 2026-10-16
 * @description "Show this in its search screen" for every entity modal, wherever
 *              the modal was opened from.
 *
 * The button used to exist only when a caller passed `onOpenInSearch` — which
 * EntityModalProvider did and no screen opening a modal directly ever did, so
 * a sales order opened from the transport board or a PO opened from a bill had
 * no way to its list. Each modal now asks this hook instead: it offers the
 * button unless the user is ALREADY on that record's search screen (where the
 * row is right behind the modal), and works out the reveal itself from the
 * shell's navigation (SearchRevealContext, provided by Dashboard).
 *
 * The receiving screen takes the term with useReveal(target.menu) and seeds
 * its filters and page from it (utils/revealInSearch.js).
 *
 * The target's `tab` is passed with the navigation, so a page left on another
 * of its tabs (Sales Orders on Pricing, Contacts on Reps) switches to the list
 * — otherwise the list never mounts, and the handoff is never taken.
 */

import { createContext, useContext, useCallback } from 'react';
import { revealInSearch } from '../utils/revealInSearch';

/*
 * Where each kind of record is listed, and what to search it by. `page` and
 * `tab` are where that list actually lives (for "is the user already there?");
 * `menu` is the route navigated to, which the shell resolves to the page + tab.
 */
export const SEARCH_TARGETS = {
  invoice:        { menu: 'search-invoices', page: 'search-invoices', label: 'Invoices', ref: r => r?.invoice_no },
  credit_note:    { menu: 'search-invoices', page: 'search-invoices', label: 'Invoices', ref: r => r?.invoice_no },
  purchase_order: { menu: 'search-purchase-orders', page: 'search-purchase-orders', tab: 'orders', label: 'Purchase Orders', ref: r => r?.purchaseOrderNumber || r?.purchase_order_number },
  customer_order: { menu: 'search-customer-orders', page: 'search-customer-orders', tab: 'orders', label: 'Sales Orders', ref: r => r?.order_number },
  // A bill's number is its supplier's invoice number (supplier_reference); bill_number is never filled (Cam, 16 Oct 2026).
  bill:           { menu: 'search-bills', page: 'search-bills', label: 'Bills', ref: r => r?.supplier_reference || r?.bill_number },
  contact:        { menu: 'search-contacts', page: 'search-contacts', tab: 'contacts', label: 'Contacts', ref: r => r?.account_no || r?.company_name },
  inventory_item: { menu: 'search-inventory', page: 'search-inventory', tab: 'inventory', label: 'Inventory', ref: r => r?.item_code || r?.item_name },
  payment:        { menu: 'search-payments', page: 'credit-limits', tab: 'payments', label: 'Payments', ref: r => r?.reference || r?.contact_name || r?.payment_reference },
  haulage_job:    { menu: 'haulage-jobs', page: 'haulage-jobs', label: 'Haulage Jobs', ref: r => r?.job_number },
  standing_order: { menu: 'standing-orders', page: 'standing-orders', label: 'Standing Orders', ref: r => r?.reference || r?.customer_name || r?.company_name },
  driver:         { menu: 'transport-drivers', page: 'transport-settings', tab: 'drivers', label: 'Drivers', ref: r => r?.name },
  vehicle:        { menu: 'transport-vehicles', page: 'transport-settings', tab: 'vehicles', label: 'Vehicles', ref: r => r?.registration },
  sales_rep:      { menu: 'reps', page: 'search-contacts', tab: 'reps', label: 'Reps', ref: r => r?.name }
};

/** { onNavigate, activeMenu, activeTab } — provided by Dashboard. */
export const SearchRevealContext = createContext(null);

/**
 * @param {string}   type           a SEARCH_TARGETS key
 * @param {Function} [passed]       an onOpenInSearch the caller supplied (wins)
 * @param {Function} [onClose]      closes the modal before navigating
 * @returns {{ open: ((record) => void) | null, label: string }}
 */
export default function useOpenInSearch(type, passed = null, onClose = null) {
  const ctx = useContext(SearchRevealContext);
  const target = SEARCH_TARGETS[type];

  const derived = useCallback((record) => {
    const term = target?.ref?.(record);
    revealInSearch(ctx?.onNavigate, target?.menu, term, onClose, { tab: target?.tab });
  }, [ctx, target, onClose]);

  const label = target ? `Find in ${target.label}` : 'Find in list';
  if (typeof passed === 'function') return { open: passed, label };
  if (!target || typeof ctx?.onNavigate !== 'function') return { open: null, label };
  // Already on the list this record lives in: the row is right behind us.
  const here = ctx.activeMenu === target.page && (!target.tab || ctx.activeTab === target.tab || !ctx.activeTab);
  return { open: here ? null : derived, label };
}
